import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { BarChart3, ListChecks, Scale, Newspaper, Languages, Calculator, Brain, FileText, History, Bot } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { Header } from "@/components/Header";
import { Dashboard } from "@/pages/Dashboard";
import { MCQPractice } from "@/pages/MCQPractice";
import { ExamSection } from "@/pages/exam/ExamSection";
import { PracticeHistory } from "@/pages/PracticeHistory";
import ArticleGrid from "@/pages/constitution/ArticleGrid";
import NewsGrid from "@/pages/news/NewsGrid";
import EnglishGrid from "@/pages/english/EnglishGrid";
import { MathsPractice } from "@/pages/maths/MathsPractice";
import { ReasoningPractice } from "@/pages/reasoning/ReasoningPractice";
import { AIAssistantGrid } from "@/pages/ai/AIAssistantGrid";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: any }>;
}

export function MainApp() {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const menuItems: MenuItem[] = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "ai-assistant", label: "Sanmaya AI", icon: Bot },
    { id: "mcq", label: "MCQ Practice", icon: ListChecks },
    { id: "constitution", label: "Constitution", icon: Scale },
    { id: "current-affairs", label: "News", icon: Newspaper },
    { id: "english", label: "English Grammar", icon: Languages },
    { id: "mathematics", label: "Mathematics", icon: Calculator },
    { id: "reasoning", label: "Reasoning", icon: Brain },
    { id: "exam", label: "Take Exam", icon: FileText },
    { id: "history", label: "Practice History", icon: History },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <Dashboard onSectionChange={handleSectionChange} />;
      case "ai-assistant":
        return <AIAssistantGrid />;
      case "mcq":
        return <MCQPractice />;
      case "exam":
        return <ExamSection onSectionChange={handleSectionChange} />;
      case "history":
        return <PracticeHistory />;
      case "constitution":
        return <ArticleGrid />;
      case "current-affairs":
        return <NewsGrid />;
      case "english":
        return <EnglishGrid />;
      case "mathematics":
        return <MathsPractice />;
      case "reasoning":
        return <ReasoningPractice />;
      default:
        return <Dashboard onSectionChange={handleSectionChange} />;
    }
  };

  const metaTitles: { [key: string]: string } = {
    dashboard: "Dashboard | GCEP",
    mcq: "General Knowledge | GCEP",
    mathematics: "Mathematics Practice | GCEP",
    reasoning: "Reasoning Practice | GCEP",
    constitution: "Indian Constitution Study | GCEP",
    "current-affairs": "Current Affairs & News | GCEP",
    english: "English Grammar Practice | GCEP",
    "ai-assistant": "Sanmaya AI | GCEP",
    exam: "Take Full-Length Exam | GCEP",
    history: "Practice History & Reports | GCEP",
  };

  return (
    <div className="flex min-h-screen gcep-gradient-bg">
      <Helmet>
        <title>{metaTitles[activeSection] || "Dashboard | GCEP"}</title>
        <meta
          name="description"
          content="Track progress, practice MCQs, take exams, and study for competitive exams with GCEP. Available in English and Gujarati."
        />
        <meta
          name="keywords"
          content="GCEP, MCQ practice, competitive exams, English, Mathematics, Reasoning, Constitution, Current Affairs, exam preparation, Gujarati"
        />
        <meta name="author" content="GCEP Group" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={metaTitles[activeSection] || "Dashboard | GCEP"} />
        <meta
          property="og:description"
          content="Track progress, practice MCQs, take exams, and study for competitive exams with GCEP. Available in English and Gujarati."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        menuItems={menuItems}
      />

      <main className="flex-1 lg:ml-72 transition-all duration-300">
        <Header onSidebarToggle={handleSidebarToggle} />
        {renderContent()}
      </main>
    </div>
  );
}